
import { FileText, Download } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface Section {
  id: string;
  name: string;
  position: number;
}

interface Document {
  id: string;
  name: string;
  display_name?: string;
  file_path: string;
  section_id: string;
  created_at: string;
  size: number;
  position: number;
  content_type: string;
  created_by: string | null;
  creator_email: string | null;
  creator_name: string | null;
  organization_id: string;
}

interface MemberDocumentGridViewProps {
  selectedSection: Section;
  documents: Document[];
  onDownload: (doc: Document) => void;
  isLoadingDocuments: boolean;
}

export const MemberDocumentGridView = ({
  selectedSection,
  documents,
  onDownload,
  isLoadingDocuments
}: MemberDocumentGridViewProps) => {
  const formatDate = (dateString: string) => {
    try {
      return `Updated: ${formatDistanceToNow(new Date(dateString))} ago`;
    } catch {
      return "Updated: Unknown";
    }
  };

  return (
    <div className="p-6">
      {/* Documents Grid */}
      {isLoadingDocuments ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2F80ED]"></div>
          <span className="ml-2 text-[#637588]">Loading documents...</span>
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-[#637588] mx-auto mb-4" />
          <p className="text-[#637588] mb-2">No documents found</p>
          <p className="text-sm text-[#637588]/60">No documents available in this section</p>
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-6">
          {documents.map((doc) => (
            <div key={doc.id} className="flex flex-col">
              <div className="relative group mb-3">
                <div 
                  className="w-full bg-[#f0f2f4] h-36 rounded-lg flex items-center justify-center cursor-pointer hover:bg-[#e6eef5] transition-colors border border-[#dce0e5]"
                  onClick={() => onDownload(doc)}
                >
                  <FileText className="h-12 w-12 text-[#2F80ED]" />
                </div>
                
                {/* Download button overlay */}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDownload(doc);
                    }}
                    className="h-8 w-8 bg-white/95 hover:bg-white border border-[#dce0e5] shadow-sm rounded flex items-center justify-center"
                    title="Download"
                  >
                    <Download className="h-4 w-4 text-[#2F80ED]" />
                  </button>
                </div>
              </div>
              
              <div>
                <p className="text-[#111418] text-sm font-medium leading-normal truncate mb-1" title={doc.display_name || doc.name}>
                  {doc.display_name || doc.name}
                </p>
                {doc.display_name && (
                  <p className="text-[#637588] text-xs font-normal leading-normal truncate mb-1" title={doc.name}>
                    {doc.name}
                  </p>
                )}
                <p className="text-[#637588] text-xs font-normal leading-normal">
                  {formatDate(doc.created_at)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
